import { useCallback, useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusCard } from '@/components/ui/card/StatusCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type {
  AdminApiClient,
  AdminUser,
  ListUsersResponse,
  ListCompaniesResponse,
  ListModulesResponse,
} from '@/features/admin/api/AdminApiClient';
import { MemberRow } from './MemberRow';
import { MemberFormDialog, type AssignableRole } from './MemberFormDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { DeleteMemberDialog } from './DeleteMemberDialog';

export type MembersTabProps = { client: AdminApiClient; currentUserEmail: string };

export function MembersTab({ client, currentUserEmail }: MembersTabProps) {
  const { t } = useTranslation();
  const { tokenService, reloadProfile } = useAuth();

  // Rights ceiling: an admin may only assign their own role level and modules,
  // and manages only their own company. The backend enforces all of this too.
  const isSuperAdmin = (tokenService?.getRole?.() ?? 'user') === 'super-admin';
  const ownCompany = tokenService?.getCompany?.() ?? null;

  const usersCall = useApiCall<ListUsersResponse>({ apiFn: useCallback(() => client.listUsers(), [client]) });
  // Listing companies is a super-admin route; admins are pinned to their own company.
  const companiesCall = useApiCall<ListCompaniesResponse>({
    apiFn: useCallback(() => client.listCompanies(), [client]),
    immediate: isSuperAdmin,
  });
  const modulesCall = useApiCall<ListModulesResponse>({ apiFn: useCallback(() => client.listModules(), [client]) });

  const users = usersCall.data?.users ?? [];
  const companyNames = isSuperAdmin
    ? (companiesCall.data?.companies ?? []).map((c) => c.name)
    : ownCompany
      ? [ownCompany]
      : [];
  const moduleKeys = modulesCall.data?.modules ?? [];

  const offeredModules = isSuperAdmin ? moduleKeys : ((tokenService?.getModules?.() ?? []) as string[]);
  const allowedRoles: AssignableRole[] = isSuperAdmin ? ['user', 'admin'] : ['user'];

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<AdminUser | undefined>(undefined);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const refetch = usersCall.retry;
  // The edited member may be the signed-in user: their role, modules or company change now.
  const handleMemberSaved = () => {
    refetch();
    void reloadProfile();
  };

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase();
    const matchesSearch = !q || u.email.toLowerCase().includes(q) || name.includes(q);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesCompany = companyFilter === 'all' || u.companyName === companyFilter;
    return matchesSearch && matchesRole && matchesCompany;
  });

  function openAdd() { setFormMode('create'); setSelected(undefined); setFormOpen(true); }
  function openEdit(u: AdminUser) { setFormMode('edit'); setSelected(u); setFormOpen(true); }
  function openReset(u: AdminUser) { setSelected(u); setResetOpen(true); }
  function openDelete(u: AdminUser) { setSelected(u); setDeleteOpen(true); }

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="min-w-[220px] flex-1"
          placeholder={t('administration.members.search')}
          aria-label={t('administration.members.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[150px]" aria-label={t('administration.members.roleFilter')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('administration.members.allRoles')}</SelectItem>
            <SelectItem value="user">{t('administration.roles.user')}</SelectItem>
            <SelectItem value="admin">{t('administration.roles.admin')}</SelectItem>
            <SelectItem value="super-admin">{t('administration.roles.superAdmin')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={companyFilter} onValueChange={setCompanyFilter}>
          <SelectTrigger className="w-[170px]" aria-label={t('administration.members.companyFilter')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('administration.members.allCompanies')}</SelectItem>
            {companyNames.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="default" size="sm" onClick={openAdd}>
          <Plus aria-hidden />
          {t('administration.member.add')}
        </Button>
      </div>

      {usersCall.error && !usersCall.isLoading && (
        <>
          <StatusCard type="error" message={usersCall.error} />
          <Button variant="outline" size="sm" className="w-fit" onClick={refetch}>
            {t('administration.retry')}
          </Button>
        </>
      )}

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="eyebrow border-b border-border text-left">
              <th className="px-4 py-3 font-semibold">{t('administration.members.colMember')}</th>
              <th className="px-4 py-3 font-semibold">{t('administration.members.colRole')}</th>
              <th className="px-4 py-3 font-semibold">{t('administration.members.colCompany')}</th>
              <th className="px-4 py-3 font-semibold">{t('administration.members.colAccess')}</th>
              <th className="px-4 py-3 font-semibold">{t('administration.members.colLastLogin')}</th>
              <th className="px-4 py-3 text-right font-semibold">{t('administration.members.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {usersCall.isLoading &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="border-b border-border" data-testid="member-skeleton">
                  <td className="px-4 py-3" colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))}
            {!usersCall.isLoading && !usersCall.error && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {t('administration.members.empty')}
                </td>
              </tr>
            )}
            {!usersCall.isLoading &&
              filtered.map((u) => (
                <MemberRow
                  key={u.email}
                  user={u}
                  isSelf={u.email === currentUserEmail}
                  locked={isSuperAdmin ? undefined : u.role !== 'user'}
                  onEdit={openEdit}
                  onReset={openReset}
                  onDelete={openDelete}
                  t={t}
                />
              ))}
          </tbody>
        </table>
      </div>

      <MemberFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        member={selected}
        companies={companyNames}
        modules={offeredModules}
        client={client}
        onSuccess={handleMemberSaved}
        isSelf={selected?.email === currentUserEmail}
        allowedRoles={allowedRoles}
      />
      {selected && (
        <ResetPasswordDialog
          open={resetOpen}
          onOpenChange={setResetOpen}
          email={selected.email}
          client={client}
          onSuccess={refetch}
        />
      )}
      {selected && (
        <DeleteMemberDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          member={selected}
          client={client}
          onSuccess={refetch}
        />
      )}
    </div>
  );
}
